import React from 'react';
import { render, fireEvent, act } from '@testing-library/react';

// Custom mock for pusher-js so each test can inspect the bindings and
// drive event handlers. This overrides the global jest.mock in
// test/js/setup.js for this file only.
jest.mock('pusher-js', () => {
    const instances = [];
    const ctor = jest.fn().mockImplementation((key, opts) => {
        const channelBindings = {};
        const connectionBindings = {};
        const channel = {
            bind: jest.fn((eventName, handler) => {
                channelBindings[eventName] = handler;
            }),
            unbind: jest.fn(),
        };
        const instance = {
            key,
            opts,
            channel,
            channelBindings,
            connectionBindings,
            subscribedChannelName: null,
            subscribe: jest.fn((name) => {
                instance.subscribedChannelName = name;
                return channel;
            }),
            unsubscribe: jest.fn(),
            disconnect: jest.fn(),
            connection: {
                bind: jest.fn((eventName, handler) => {
                    connectionBindings[eventName] = handler;
                }),
                unbind: jest.fn(),
            },
        };
        instances.push(instance);
        return instance;
    });
    ctor.__instances = instances;
    ctor.__reset = () => { ctor.mockClear(); instances.length = 0; };
    return ctor;
});

import Pusher from 'pusher-js';
import PlaylistSlideshow from './playlist_slideshow';

// Mock fullscreen API
document.documentElement.requestFullscreen = jest.fn().mockResolvedValue();
document.exitFullscreen = jest.fn().mockResolvedValue();
Object.defineProperty(document, 'fullscreenElement', { value: null, writable: true });

const mockItems = [
    { billboard_media_item: { url: 'https://example.com/1.jpg' } },
    { billboard_media_item: { url: 'https://example.com/2.jpg' } },
    { billboard_media_item: { url: 'https://example.com/3.jpg' } },
];

describe('PlaylistSlideshow', () => {
    beforeEach(() => {
        jest.useFakeTimers();
        Pusher.__reset();
        // Provide a fetch shim for the state-recovery call so tests that
        // exercise the reconnect path don't reach a real network.
        global.fetch = jest.fn().mockResolvedValue({
            ok: true,
            json: () => Promise.resolve({ live: false }),
        });
    });

    afterEach(() => {
        jest.useRealTimers();
        delete global.fetch;
    });

    it('renders the first image', () => {
        const { container } = render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        const img = container.querySelector('img');
        expect(img.src).toBe('https://example.com/1.jpg');
    });

    it('shows slide counter', () => {
        const { getByText } = render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        // Move mouse to show controls (after grace period)
        jest.advanceTimersByTime(1100);
        fireEvent.mouseMove(document.querySelector('[style*="position: fixed"]'));
        expect(getByText('1 / 3')).toBeTruthy();
    });

    it('advances to next slide after 10 seconds', () => {
        const { container } = render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        // 10s interval fires the fade-out, then the 500ms fade timer
        // swaps the src at the trough (#322).
        jest.advanceTimersByTime(10000 + 500);
        const img = container.querySelector('img');
        expect(img.src).toBe('https://example.com/2.jpg');
    });

    it('loops back to first slide', () => {
        const { container } = render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        // Three full advance cycles (10s interval + 500ms fade each).
        jest.advanceTimersByTime((10000 + 500) * 3);
        const img = container.querySelector('img');
        expect(img.src).toBe('https://example.com/1.jpg');
    });

    it('calls onClose when Escape is pressed', () => {
        const onClose = jest.fn();
        render(<PlaylistSlideshow items={mockItems} onClose={onClose} />);
        fireEvent.keyDown(document, { key: 'Escape' });
        expect(onClose).toHaveBeenCalled();
    });

    it('advances on right arrow key', () => {
        const { container } = render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        fireEvent.keyDown(document, { key: 'ArrowRight' });
        // 500ms fade completes before the src swap (#322).
        jest.advanceTimersByTime(500);
        const img = container.querySelector('img');
        expect(img.src).toBe('https://example.com/2.jpg');
    });

    it('goes back on left arrow key', () => {
        const { container } = render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        fireEvent.keyDown(document, { key: 'ArrowLeft' });
        jest.advanceTimersByTime(500);
        // Wraps to last slide
        const img = container.querySelector('img');
        expect(img.src).toBe('https://example.com/3.jpg');
    });

    it('does not show controls during grace period', () => {
        render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        const overlay = document.querySelector('[style*="position: fixed"]');
        fireEvent.mouseMove(overlay);
        // Close button should have opacity 0 during grace period
        const closeBtn = overlay.querySelector('button');
        expect(closeBtn.style.opacity).toBe('0');
    });

    it('returns null with empty items', () => {
        const { container } = render(<PlaylistSlideshow items={[]} onClose={jest.fn()} />);
        expect(container.firstChild).toBeNull();
    });

    it('requests fullscreen on mount', () => {
        render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
        expect(document.documentElement.requestFullscreen).toHaveBeenCalled();
    });

    // ------------------------------------------------------------------
    // Per-slide display_mode (#271, #319) + crossfade (#322)
    // ------------------------------------------------------------------

    describe('display_mode (#319)', () => {
        // The TV-display route hardcoded object-fit: contain, which
        // rendered every mode as scale_to_fit. These tests pin the
        // wire-level contract from docs/billboard_image_display_modes.md.
        const itemFor = (mode) => ({
            display_mode: mode,
            billboard_media_item: { url: 'https://example.com/1.jpg' },
        });

        it('scale_to_fit → object-fit contain', () => {
            const { container } = render(
                <PlaylistSlideshow items={[itemFor('scale_to_fit')]} onClose={jest.fn()} />
            );
            const img = container.querySelector('img');
            expect(img.style.objectFit).toBe('contain');
        });

        it('scale_to_fill → object-fit cover', () => {
            const { container } = render(
                <PlaylistSlideshow items={[itemFor('scale_to_fill')]} onClose={jest.fn()} />
            );
            const img = container.querySelector('img');
            expect(img.style.objectFit).toBe('cover');
        });

        it('stretch → object-fit fill', () => {
            const { container } = render(
                <PlaylistSlideshow items={[itemFor('stretch')]} onClose={jest.fn()} />
            );
            const img = container.querySelector('img');
            expect(img.style.objectFit).toBe('fill');
        });

        it('center → object-fit none + centered', () => {
            const { container } = render(
                <PlaylistSlideshow items={[itemFor('center')]} onClose={jest.fn()} />
            );
            const img = container.querySelector('img');
            expect(img.style.objectFit).toBe('none');
            expect(img.style.objectPosition).toBe('center');
        });

        it('scale_and_blur → two stacked img layers (blurred fill + contained foreground)', () => {
            const { container } = render(
                <PlaylistSlideshow items={[itemFor('scale_and_blur')]} onClose={jest.fn()} />
            );
            const imgs = container.querySelectorAll('img');
            expect(imgs.length).toBe(2);
            // Background layer: cover + blur + scale-up
            const bg = imgs[0];
            expect(bg.getAttribute('aria-hidden')).toBe('true');
            expect(bg.style.objectFit).toBe('cover');
            expect(bg.style.filter).toContain('blur');
            expect(bg.style.transform).toContain('scale');
            // Foreground layer: contain
            const fg = imgs[1];
            expect(fg.style.objectFit).toBe('contain');
        });

        it('null / missing display_mode falls back to scale_to_fit', () => {
            const { container } = render(
                <PlaylistSlideshow
                    items={[{ billboard_media_item: { url: 'https://example.com/x.jpg' } }]}
                    onClose={jest.fn()}
                />
            );
            const img = container.querySelector('img');
            expect(img.style.objectFit).toBe('contain');
        });
    });

    describe('crossfade transitions (#322)', () => {
        it('slide frame declares a 500ms opacity transition', () => {
            const { container } = render(
                <PlaylistSlideshow items={mockItems} onClose={jest.fn()} />
            );
            const frame = container.querySelector('[data-testid="slide-frame"]');
            expect(frame).not.toBeNull();
            // React inline-style camel-case → CSS: `transition: opacity
            // 500ms linear`. Assert on the parts to stay robust to the
            // browser's normalization of transition serialization.
            expect(frame.style.transition).toContain('opacity');
            expect(frame.style.transition).toContain('500ms');
            expect(frame.style.opacity).toBe('1');
        });

        it('auto-advance: opacity drops to 0 during the fade window, src swaps at trough, opacity returns to 1', () => {
            const { container } = render(
                <PlaylistSlideshow items={mockItems} onClose={jest.fn()} />
            );
            const initialFrame = container.querySelector('[data-testid="slide-frame"]');
            expect(initialFrame.style.opacity).toBe('1');
            expect(container.querySelector('img').src).toBe('https://example.com/1.jpg');

            // Interval fires at 10s → begins fade-out. Src has not yet
            // swapped; opacity is 0 for the 500ms window.
            act(() => {
                jest.advanceTimersByTime(10000);
            });
            const midFadeFrame = container.querySelector('[data-testid="slide-frame"]');
            expect(midFadeFrame.style.opacity).toBe('0');
            expect(container.querySelector('img').src).toBe('https://example.com/1.jpg');

            // 500ms later the swap happens and the new slide fades up.
            act(() => {
                jest.advanceTimersByTime(500);
            });
            const postSwapFrame = container.querySelector('[data-testid="slide-frame"]');
            expect(postSwapFrame.style.opacity).toBe('1');
            expect(container.querySelector('img').src).toBe('https://example.com/2.jpg');
        });

        it('keyboard advance also fades', () => {
            const { container } = render(
                <PlaylistSlideshow items={mockItems} onClose={jest.fn()} />
            );
            act(() => {
                fireEvent.keyDown(document, { key: 'ArrowRight' });
            });
            // Mid-fade: still slide 1, opacity 0.
            expect(container.querySelector('[data-testid="slide-frame"]').style.opacity).toBe('0');
            expect(container.querySelector('img').src).toBe('https://example.com/1.jpg');

            act(() => {
                jest.advanceTimersByTime(500);
            });
            expect(container.querySelector('[data-testid="slide-frame"]').style.opacity).toBe('1');
            expect(container.querySelector('img').src).toBe('https://example.com/2.jpg');
        });
    });

    // ------------------------------------------------------------------
    // Live stream override (#253)
    // ------------------------------------------------------------------

    describe('live stream override (#253)', () => {
        it('does NOT subscribe to Pusher when billboardOutputId is omitted', () => {
            render(<PlaylistSlideshow items={mockItems} onClose={jest.fn()} />);
            expect(Pusher).not.toHaveBeenCalled();
            expect(Pusher.__instances.length).toBe(0);
        });

        it('subscribes to billboard-output-<id> when billboardOutputId is provided', () => {
            render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            expect(Pusher).toHaveBeenCalledTimes(1);
            const instance = Pusher.__instances[0];
            expect(instance.subscribe).toHaveBeenCalledWith('billboard-output-42');
            expect(Object.keys(instance.channelBindings).sort()).toEqual(
                ['live_stream_source.ended', 'live_stream_source.went_live']
            );
        });

        it('on went_live: stops the timer, mounts the LiveStreamOverlay iframe with the right video id', () => {
            const { container } = render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            const instance = Pusher.__instances[0];
            act(() => {
                instance.channelBindings['live_stream_source.went_live']({
                    source_id: 7,
                    billboard_output_id: 42,
                    channel_id: 'UCabcdefghijklmnopqrstuv',
                    video_id: 'dQw4w9WgXcQ',
                    provider: 'youtube_live',
                });
            });

            // YouTube iframe is mounted (#254)
            const iframe = container.querySelector('iframe');
            expect(iframe).not.toBeNull();
            expect(iframe.src).toContain('youtube.com/embed/dQw4w9WgXcQ');
            expect(iframe.src).toMatch(/autoplay=1/);
            expect(iframe.src).toMatch(/mute=1/);

            // Original slide image is no longer rendered
            expect(container.querySelector('img')).toBeNull();
            // Payload pass-through via data attributes round-trip from
            // PlaylistSlideshow state through LiveStreamOverlay props.
            const overlay = container.querySelector('[data-live-stream-override="true"]');
            expect(overlay).not.toBeNull();
            expect(overlay.getAttribute('data-source-id')).toBe('7');
            expect(overlay.getAttribute('data-video-id')).toBe('dQw4w9WgXcQ');
            expect(overlay.getAttribute('data-channel-id')).toBe('UCabcdefghijklmnopqrstuv');

            // Timer must be stopped — advancing 10s does NOT change anything
            jest.advanceTimersByTime(20000);
            expect(container.querySelector('iframe')).not.toBeNull();
            expect(container.querySelector('img')).toBeNull();
        });

        it('on ended: two-phase fade — overlay stays mounted, opacity flips to 0, transitionEnd then resumes playlist from index 0', () => {
            const { container } = render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            // Advance one slide first so we can assert the reset.
            // 10000ms interval + 500ms fade (#322).
            jest.advanceTimersByTime(10000 + 500);
            expect(container.querySelector('img').src).toBe('https://example.com/2.jpg');

            const instance = Pusher.__instances[0];
            // Go live, then end
            act(() => {
                instance.channelBindings['live_stream_source.went_live']({
                    source_id: 7,
                    billboard_output_id: 42,
                    channel_id: 'UCabc',
                    video_id: 'vid',
                    provider: 'youtube_live',
                });
            });
            expect(container.querySelector('img')).toBeNull();
            const overlay = container.querySelector('[data-live-stream-override="true"]');
            expect(overlay.style.opacity).toBe('1');

            act(() => {
                instance.channelBindings['live_stream_source.ended']({
                    source_id: 7,
                    billboard_output_id: 42,
                    channel_id: 'UCabc',
                });
            });

            // Phase 1: overlay still mounted, opacity transitioning to 0.
            // Playlist has NOT resumed yet.
            const fadingOverlay = container.querySelector('[data-live-stream-override="true"]');
            expect(fadingOverlay).not.toBeNull();
            expect(fadingOverlay.style.opacity).toBe('0');
            expect(container.querySelector('img')).toBeNull();

            // Phase 2: transitionend fires after the 1s opacity transition.
            act(() => {
                fireEvent.transitionEnd(fadingOverlay, { propertyName: 'opacity' });
            });

            // Back to slide 1 (index 0), overlay gone.
            expect(container.querySelector('[data-live-stream-override="true"]')).toBeNull();
            expect(container.querySelector('img').src).toBe('https://example.com/1.jpg');
            // Timer restarted — 10s interval + 500ms fade advances to slide 2.
            jest.advanceTimersByTime(10000 + 500);
            expect(container.querySelector('img').src).toBe('https://example.com/2.jpg');
        });

        it('cleans up Pusher resources on unmount', () => {
            const { unmount } = render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            const instance = Pusher.__instances[0];
            unmount();
            expect(instance.channel.unbind).toHaveBeenCalled();
            expect(instance.unsubscribe).toHaveBeenCalledWith('billboard-output-42');
            expect(instance.disconnect).toHaveBeenCalled();
        });

        it('handles ended fired while not in override (defensive, no crash)', () => {
            const { container } = render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            const instance = Pusher.__instances[0];
            expect(() => {
                act(() => {
                    instance.channelBindings['live_stream_source.ended']({
                        source_id: 7,
                        billboard_output_id: 42,
                        channel_id: 'UCabc',
                    });
                });
            }).not.toThrow();
            // Still showing first slide; timer still running.
            expect(container.querySelector('img').src).toBe('https://example.com/1.jpg');
        });

        it('handles went_live mid-transition: timer stops cleanly', () => {
            const { container } = render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            // Advance partway through a slide (5s in)
            jest.advanceTimersByTime(5000);
            const instance = Pusher.__instances[0];
            act(() => {
                instance.channelBindings['live_stream_source.went_live']({
                    source_id: 9,
                    billboard_output_id: 42,
                    channel_id: 'UCxyz',
                    video_id: 'vid9',
                    provider: 'youtube_live',
                });
            });
            // Override shown (iframe mounted)
            expect(container.querySelector('[data-live-stream-override="true"]')).not.toBeNull();
            // Push past where the next slide would have advanced; still on override.
            jest.advanceTimersByTime(60000);
            expect(container.querySelector('[data-live-stream-override="true"]')).not.toBeNull();
            expect(container.querySelector('img')).toBeNull();
        });

        it('went_live with nil video_id: shows fallback (no broken iframe), stays in override until ended', () => {
            const { container } = render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            const instance = Pusher.__instances[0];
            // Race condition flagged on #253 QA: worker wrote is_live before
            // youtube_live_video_id. Pusher event arrives with video_id=null.
            act(() => {
                instance.channelBindings['live_stream_source.went_live']({
                    source_id: 7,
                    billboard_output_id: 42,
                    channel_id: 'UCabc',
                    video_id: null,
                    provider: 'youtube_live',
                });
            });
            // Override is active but iframe is NOT mounted (would have a
            // broken src). Fallback message is shown instead.
            expect(container.querySelector('iframe')).toBeNull();
            const overlay = container.querySelector('[data-live-stream-override="true"]');
            expect(overlay).not.toBeNull();
            expect(overlay.textContent).toMatch(/Live stream temporarily unavailable/i);
            // Playlist image still suppressed.
            expect(container.querySelector('img')).toBeNull();
        });

        it('state-recovery {live: false} also triggers fade (not a hard cut)', async () => {
            const { container } = render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            const instance = Pusher.__instances[0];
            // Boot into override via went_live first.
            act(() => {
                instance.channelBindings['live_stream_source.went_live']({
                    source_id: 7,
                    billboard_output_id: 42,
                    channel_id: 'UCabc',
                    video_id: 'vid7',
                    provider: 'youtube_live',
                });
            });
            const overlay = container.querySelector('[data-live-stream-override="true"]');
            expect(overlay).not.toBeNull();
            expect(overlay.style.opacity).toBe('1');

            // Simulate reconnect: server reports stream is no longer live.
            global.fetch.mockClear();
            global.fetch.mockResolvedValueOnce({
                ok: true,
                json: () => Promise.resolve({
                    live: false,
                    source_id: 7,
                    channel_id: 'UCabc',
                }),
            });
            await act(async () => {
                instance.connectionBindings['connected']();
                await Promise.resolve();
                await Promise.resolve();
            });

            // Should be in fade (phase 1), not a hard cut.
            const fading = container.querySelector('[data-live-stream-override="true"]');
            expect(fading).not.toBeNull();
            expect(fading.style.opacity).toBe('0');
            expect(container.querySelector('img')).toBeNull();

            // Phase 2: transitionend tears down the override.
            act(() => {
                fireEvent.transitionEnd(fading, { propertyName: 'opacity' });
            });
            expect(container.querySelector('[data-live-stream-override="true"]')).toBeNull();
            expect(container.querySelector('img').src).toBe('https://example.com/1.jpg');
        });

        it('queries the state-recovery endpoint after Pusher reconnect', async () => {
            render(
                <PlaylistSlideshow
                    items={mockItems}
                    onClose={jest.fn()}
                    billboardOutputId={42}
                />
            );
            const instance = Pusher.__instances[0];
            expect(typeof instance.connectionBindings['connected']).toBe('function');

            // Reset fetch mock; simulate "no live" recovery response.
            global.fetch.mockClear();
            global.fetch.mockResolvedValueOnce({
                ok: true,
                json: () => Promise.resolve({ live: false }),
            });

            await act(async () => {
                instance.connectionBindings['connected']();
                await Promise.resolve();
            });

            expect(global.fetch).toHaveBeenCalledWith(
                expect.stringContaining('/api/v2/devices/42/live_stream_state'),
                expect.any(Object)
            );
        });
    });
});
